import type { ErrorView, QueryView } from '@krp/client-runtime'

/**
 * Модели окна «Кассиры» (vm + actions): экран получает их пропсами и не знает ни store, ни HTTP. Собирает их binding приложения
 * (`apps/web/src/bindings/own`) из кэша запросов и намерения человека.
 */

/** Почему запись выключена: нет права на этом ресурсе. Кнопки видны всегда, причина написана рядом. */
export const CASHIERS_WRITE_DISABLED = 'Нет права вести кассиров этой кассы — его выдаёт администратор в «Доступе» (receipts.cashiers.manage).'

/** Нет права отзывать доступ в Cashiers: архивирование идёт, но PIN кассира остаётся действовать — человек должен это знать. */
export const CASHIERS_REVOKE_DISABLED =
  'У вас нет права настраивать доступ кассиров (platform:cashiers:manage): при архивировании действующий доступ кассира (допуск по PIN) НЕ будет отозван. Попросите администратора отозвать их в вкладке «Кассиры» раздела «Чеки и кассиры».'

export interface CashierDraft {
  /** `null` — новый кассир. */
  id: string | null
  name: string
  /** Версия узла на момент открытия формы: расхождение с сервером даст `cashier-revision-conflict`. */
  revision: number | null
}

export const NEW_CASHIER_DRAFT: CashierDraft = { id: null, name: '', revision: null }

export interface CashierListRow {
  id: string
  name: string
  archived: boolean
  /** «Действует» / «В архиве» — слово рядом со значком, цвет не единственный признак. */
  statusText: string
  revisionText: string
}

/** Подтверждение архивирования: что произойдёт, и ход выполнения (отзыв доступа, затем архив). */
export interface CashierArchiveVm {
  cashierId: string
  name: string
  working: boolean
  /** Что сейчас делается: «Отзываю PIN и доступ…», «Записываю в архив…»; `null` — ничего. */
  stage: string | null
  /** Отзыв доступа не будет сделан (нет права) — предупреждение до подтверждения. */
  revokeWarning: string | null
  /** Отказ шага — дословно, с кодом и номером запроса. */
  error: { title: string; view: ErrorView } | null
}

export interface OwnCashiersVm {
  query: QueryView
  rows: readonly CashierListRow[]
  form: { draft: CashierDraft; problem: string | null; saving: boolean; error: ErrorView | null } | null
  archive: CashierArchiveVm | null
  /** Отказ возврата из архива — у строки нет формы, поэтому над списком. */
  rowError: { title: string; view: ErrorView } | null
  /** Итог последнего действия словами (ничего не «сделано», пока сервер не ответил). */
  done: string | null
  /** Итог с оговоркой: кассир записан, но доступ не отозван (или наоборот) — остаётся на экране. */
  warning: string | null
  writeDisabledReason: string | null
}

export interface OwnCashiersActions {
  create: () => void
  edit: (cashierId: string) => void
  draftChanged: (draft: CashierDraft) => void
  save: () => void
  closeForm: () => void
  askArchive: (cashierId: string) => void
  cancelArchive: () => void
  confirmArchive: () => void
  restore: (cashierId: string) => void
  reload: () => void
  /** Окно «Доступ кассиров»: там задаётся PIN. */
  openAccess: () => void
}
